import { containerClassName, inputClassName, standaloneInputClassName } from "@/components/podkit/forms/input-classes";
import { cn } from "@/components/podkit/lib/cn";
import { Combobox } from "@ariakit/react";
import * as React from "react";

export type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "prefix" | "suffix"> & {
    prefix?: React.ReactNode;
    suffix?: React.ReactNode;
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(({ className, prefix, suffix, ...props }, ref) => {
    if (prefix || suffix) {
        return (
            <div className={containerClassName}>
                {prefix && <span className={cn("flex-shrink-0 text-content-secondary")}>{prefix}</span>}
                <input className={cn(inputClassName, className)} ref={ref} {...props} />
                {suffix && <span className={cn("flex-shrink-0 text-content-secondary")}>{suffix}</span>}
            </div>
        );
    }

    return <input className={cn(standaloneInputClassName, className)} ref={ref} {...props} />;
});
Input.displayName = "Input";

const ComboboxInput = React.forwardRef<
    React.ElementRef<typeof Combobox>,
    React.ComponentPropsWithoutRef<typeof Combobox>
>(({ className, ...props }, ref) => (
    <Combobox
        ref={ref}
        className={cn(standaloneInputClassName, className)}
        // Ariakit's Combobox manually triggers a blur event on virtually
        // blurred items, making them work as if they had actual DOM
        // focus. These blur events might happen after the corresponding
        // focus events in the capture phase, leading Radix Select to
        // close the popover. This happens because Radix Select relies on
        // the order of these captured events to discern if the focus was
        // outside the element. Since we don't have access to the
        // onInteractOutside prop in the Radix SelectContent component to
        // stop this behavior, we can turn off Ariakit's behavior here.
        onBlurCapture={(event) => {
            event.preventDefault();
            event.stopPropagation();
        }}
        {...props}
    />
));
ComboboxInput.displayName = "ComboboxInput";

export { ComboboxInput, Input };
